import { apiRequest } from "./api.client";

// ── Types (mirror app/modules/rbac/schemas.py) ──────────────────────────────

export interface Permission {
  code:        string;
  category:    string;
  label:       string;
  description: string | null;
  sort_order:  number;
  /** Two codes sharing a group_key are the view/edit halves of one page row.
   *  Null on standalone per-action permissions. */
  group_key:    string | null;
  access_level: "view" | "edit" | null;
  /** Codes this one cannot work without (e.g. Manage Appointments needs View
   *  Appointments). Absent against a backend that predates the field. */
  requires?:    string[];
}

export interface PermissionCategory {
  key:         string;
  label:       string;
  permissions: Permission[];
}

export interface Role {
  role_id:      string;
  clinic_id:    string | null;
  name:         string;
  description:  string | null;
  is_system:    boolean;
  template_key: string | null;
  cloned_from:  string | null;
  is_active:    boolean;
  created_at:   string;
  updated_at:   string;
}

export interface RoleDetail extends Role {
  permission_codes:     string[];
  assigned_user_count:  number;
}

export interface RoleCreateInput {
  name: string;
  description?: string | null;
  permission_codes?: string[];
  clone_of_role_id?: string | null;
}

export interface RoleUpdateInput {
  name?: string;
  description?: string | null;
}

// ── API ──────────────────────────────────────────────────────────────────────

export const RolesAPI = {
  getPermissionCatalog(): Promise<PermissionCategory[]> {
    return apiRequest<PermissionCategory[]>("/clinic/rbac/permission-catalog");
  },

  listRoles(): Promise<Role[]> {
    return apiRequest<Role[]>("/clinic/rbac/roles");
  },

  getRoleDetail(roleId: string): Promise<RoleDetail> {
    return apiRequest<RoleDetail>(`/clinic/rbac/roles/${roleId}`);
  },

  createRole(data: RoleCreateInput): Promise<RoleDetail> {
    return apiRequest<RoleDetail>("/clinic/rbac/roles", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  updateRole(roleId: string, data: RoleUpdateInput): Promise<RoleDetail> {
    return apiRequest<RoleDetail>(`/clinic/rbac/roles/${roleId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },

  setRolePermissions(roleId: string, permissionCodes: string[]): Promise<RoleDetail> {
    return apiRequest<RoleDetail>(`/clinic/rbac/roles/${roleId}/permissions`, {
      method: "PUT",
      body: JSON.stringify({ permission_codes: permissionCodes }),
    });
  },

  deleteRole(roleId: string): Promise<void> {
    return apiRequest<void>(`/clinic/rbac/roles/${roleId}`, { method: "DELETE" });
  },

  /** Full-set replace: roleIds becomes the user's complete role set in this clinic. */
  setStaffRoles(userId: string, roleIds: string[]): Promise<Role[]> {
    return apiRequest<Role[]>(`/clinic/rbac/staff/${userId}/roles`, {
      method: "PUT",
      body: JSON.stringify({ role_ids: roleIds }),
    });
  },
};
